import { pxToRem, pxTovW, IStyles } from '@geneo2-web/shared-ui';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

const styles: IStyles = {
  groupedHeaderEditRow: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    gap: { xs: pxToRem(4), md: pxTovW(6) },
    minWidth: 0,
    marginBottom: {
      xs: pxToRem(10),
      md: pxTovW(10),
    },
  },
  groupedHeaderPrefix: {
    fontWeight: 'bold',
    fontSize: {
      xs: pxToRem(16),
      md: pxTovW(24),
    },
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  groupedHeaderTextField: {
    flex: 1,
    minWidth: 0,
    '& .MuiInputBase-input': {
      fontWeight: 700,
      fontSize: {
        xs: pxToRem(16),
        md: pxTovW(24),
      },
      padding: {
        xs: `${pxToRem(4)} ${pxToRem(6)}`,
        md: `${pxTovW(4)} ${pxTovW(8)}`,
      },
      fontFamily: 'Poppins',
    },
  },
  saveButton: {
    flexShrink: 0,
    textTransform: 'none',
    minWidth: 'unset',
    padding: {
      xs: `${pxToRem(2)} ${pxToRem(8)}`,
      md: `${pxTovW(2)} ${pxTovW(10)}`,
    },
    fontSize: { xs: pxToRem(12), md: pxTovW(14) },
    fontWeight: 600,
    border: '1px solid',
    borderColor: 'primary.main',
    borderRadius: { xs: pxToRem(4), md: pxTovW(6) },
    '&.Mui-disabled': {
      borderColor: 'rgba(0, 0, 0, 0.26)',
    },
  },
};

export type GroupedHeaderEditorProps = {
  questionNumber: number;
  initialBody: string;
  /** Persist heading to Redux and run assessmentCreationSubmit (blur / Save / Enter). */
  onSave: (nextBody: string) => void | Promise<void>;
};

export const GroupedHeaderEditor = (props: GroupedHeaderEditorProps) => {
  const { questionNumber, initialBody, onSave } = props;
  const [draft, setDraft] = useState(initialBody);

  const isDirty = draft.trim() !== initialBody.trim();

  // ======================================================

  /** Blur and Save share the same path: Redux update + API submit with the new name. */
  const save = async () => {
    const trimmed = draft.trim();
    if (trimmed === initialBody.trim()) return;
    await onSave(trimmed);
  };

  // ======================================================

  useEffect(() => {
    setDraft(initialBody);
  }, [initialBody]);

  // ======================================================

  return (
    <Box
      sx={styles.groupedHeaderEditRow}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <Typography component="span" sx={styles.groupedHeaderPrefix}>
        {`Q${questionNumber}.`}
      </Typography>
      <TextField
        variant="standard"
        fullWidth
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void save()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void save();
          }
        }}
        sx={styles.groupedHeaderTextField}
        inputProps={{ 'aria-label': 'Group question heading' }}
      />
      <Button
        variant="outlined"
        color="primary"
        disabled={!isDirty}
        onMouseDown={(e) => {
          // Prevent blur-before-click so Save and blur don't double-submit.
          e.preventDefault();
        }}
        onClick={() => void save()}
        sx={styles.saveButton}
      >
        Save
      </Button>
    </Box>
  );
};
